import { ColumnProps } from "antd/lib/table";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import ShelfContext from "../../../../context/wms/shelf/context";
import { IShelf } from "../../../../models/wms/shelf/response";
import Space from "../../../general/antd/space";
import ViewBtn from "../../../general/table-components/actions/view-btn";
import EditBtn from "../../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../../general/table-components/actions/delete-btn";
import { ISort, IStaticFilter } from "../../../../models/base/base-query";
import { tableOnChange } from "../../../../utils/helpers/table-sorts-filters";
import SortableTable from "../../../general/table-components/sortable-table";
import EditablePickSequence from "../../../general/table-components/editable-pick-sequence";
import styles from "./styles.module.scss";

interface IProps {
  sortEnabled?: boolean;
}

const ShelfTable: React.FC<IProps> = ({ sortEnabled = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(ShelfContext);
  const [dataSource, setDataSource] = useState(list?.data ?? []);

  useEffect(() => {
    if (list?.data) {
      setDataSource(list.data);
    }
  }, [list?.data]);

  const handleDragEnd = async (activeId: number | string, overId: number | string, oldIndex: number, newIndex: number) => {
    const overItem = dataSource.find((item) => item.id === overId);
    if (overItem) {
      await actions.reorderShelf(Number(activeId), overItem.pickSequence);
    }
  };

  const columns: ColumnProps<IShelf>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: 150,
      sorter: {
        multiple: 2,
      },
      render: (value: string) => value || "-",
    },
    {
      title: t("storage_unit"),
      dataIndex: "storageUnit",
      align: "center",
      key: "storageUnitId",
      width: 180,
      sorter: {
        multiple: 2,
      },
      render: (_: any, record: IShelf) =>
        record.storageUnit?.name || record.storageUnitId || "-",
    },
    {
      title: t("scanable_code"),
      dataIndex: "scanableCode",
      align: "center",
      key: "scanableCode",
      width: 200,
      sorter: {
        multiple: 3,
      },
      render: (value: string) => value || "-",
    },
    {
      title: t("pick_sequence"),
      dataIndex: "pickSequence",
      align: "center",
      key: "pickSequence",
      sorter: {
        multiple: 4,
      },
      width: 120,
      render: (value: number, record: IShelf) =>
        sortEnabled ? (
          <EditablePickSequence
            value={value}
            onSave={async (newValue) => {
              await actions.reorderShelf(record.id, newValue);
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: t("has_bins"),
      dataIndex: "hasBins",
      align: "center",
      key: "hasBins",
      width: 120,
      sorter: {
        multiple: 5,
      },
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("yes")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("no")}
          </Tag>
        ),
    },
    {
      title: t("is_active"),
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      sorter: {
        multiple: 6,
      },
      width: 120,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("active")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("inactive")}
          </Tag>
        ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      sorter: {
        multiple: 7,
      },
      width: 150,
      render: (value: Date) => value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      title: t("actions"),
      width: 200,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
            onClick={(e) => {
              const url = `/wms/shelves/details/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <EditBtn
            onClick={(e) => {
              const url = `/wms/shelves/update/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <DeleteBtn
            onConfirm={async () => {
              await actions.deleteShelf(record?.id);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(
    query?.filters ?? []
  );

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <SortableTable<IShelf>
      dataSource={dataSource}
      columns={columns}
      loading={loading.includes("list") || list == undefined}
      showPagination={true}
      total={list?.total}
      pageSize={query.perPage}
      page={query.page}
      onPaginationChange={(page, pageSize) => {
        actions.setQuery({
          ...query,
          page,
          perPage: pageSize,
        });
      }}
      onDragEnd={handleDragEnd}
      showActiveToggle={false}
      rowKey="id"
      className={styles.table}
      sortEnabled={sortEnabled}
      onChange={(_, tableFilters, tabelSorters) => {
        tableOnChange(
          tableFilters,
          tabelSorters,
          tableFiltersProps,
          setSorts,
          setStaticFilters
        );
      }}
    />
  );
};

export default ShelfTable;
